import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SiteHeader, SiteFooter, AGENCY_CONTACT } from '../../components/website/SharedLayout';
import {
  ArrowLeft, Phone, Mail, MapPin, CheckCircle, ArrowRight,
  Star, Clock, Users, Award, Target
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

const SEOLandingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      try {
        const res = await api.get(`/website/seo-pages/${slug}`);
        setPage(res.data);
      } catch (err) {
        console.error('Error loading SEO page:', err);
        setError('Page non trouvée');
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      loadPage();
    }
  }, [slug]);

  // Update document title and meta description
  useEffect(() => {
    if (page?.content) {
      document.title = page.content.title || `${page.service} à ${page.city} | MSC L'Agence`;
      
      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', page.content.meta_description || '');
      }
    }
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="pt-32 pb-16 text-center max-w-2xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Page non trouvée</h1>
          <p className="text-slate-600 mb-8">Cette page n&apos;existe pas ou n&apos;est plus disponible.</p>
          <Link to="/site" className="text-cyan-500 hover:underline">
            Retour à l&apos;accueil
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const { content } = page;

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
                <Link to="/site" className="hover:text-white transition-colors">Accueil</Link>
                <span>/</span>
                <span className="text-cyan-400">{page.service}</span>
                <span>/</span>
                <span className="text-white">{page.city}</span>
              </div>

              <Badge className="mb-4 bg-cyan-500/20 text-cyan-300 border-cyan-500/30">
                {page.service}
              </Badge>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                {content.h1 || `${page.service} à ${page.city}`}
              </h1>
              
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                {content.intro}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-8"
                  onClick={() => navigate('/site?quote=true')}
                >
                  {content.cta_text || 'Demander un devis gratuit'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8"
                  onClick={() => window.location.href = `tel:${AGENCY_CONTACT.phone.replace(/\s/g, '')}`}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {AGENCY_CONTACT.phone}
                </Button>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                      <Clock className="w-7 h-7 text-cyan-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">+12 ans</p>
                    <p className="text-sm text-slate-400">d&apos;expérience</p>
                  </div>
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                      <Users className="w-7 h-7 text-cyan-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">+150</p>
                    <p className="text-sm text-slate-400">clients satisfaits</p>
                  </div>
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                      <Star className="w-7 h-7 text-cyan-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">4.9/5</p>
                    <p className="text-sm text-slate-400">sur Google</p>
                  </div>
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                      <Award className="w-7 h-7 text-cyan-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">+500</p>
                    <p className="text-sm text-slate-400">projets réalisés</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      {content.sections && content.sections.length > 0 && (
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4">
            {content.sections.map((section, idx) => (
              <div key={idx} className={cn("mb-16", idx === content.sections.length - 1 && "mb-0")}>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-cyan-600" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 pt-2">
                    {section.title}
                  </h2>
                </div>
                <div className="pl-16">
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Keywords */}
      {content.keywords && content.keywords.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <p className="text-sm text-slate-500 mb-4 text-center">Nos expertises :</p>
            <div className="flex flex-wrap justify-center gap-2">
              {content.keywords.map((keyword, idx) => (
                <Badge key={idx} variant="secondary" className="bg-white text-slate-700">
                  {keyword}
                </Badge>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Contactez-nous pour discuter de vos besoins en {page.service?.toLowerCase()} à {page.city}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-cyan-600 hover:bg-slate-100 rounded-full px-8"
              onClick={() => navigate('/site?quote=true')}
            >
              <Mail className="w-5 h-5 mr-2" />
              {content.cta_text || 'Demander un devis gratuit'}
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-full px-8"
              onClick={() => window.location.href = `tel:${AGENCY_CONTACT.phone.replace(/\s/g, '')}`}
            >
              <Phone className="w-5 h-5 mr-2" />
              Nous appeler
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default SEOLandingPage;
